// Stage Mail-V2 Stage 3 (2026-07-22): Conversation View — Gmail-style thread
// rendering component.
//
// Layout (Gmail 风格, 不是 Outlook 树):
//   Subject (来自父 layout, 这里是 thread 摘要)
//   "共 N 封メール"
//   ↓ 最新回复(默认展开)
//     -- sender + date + body
//   ↓ Previous N messages (折叠)
//     -- 点 → 展开一组历史 message;每条独立折叠/展开
//
// 行为规范:
// - 当前选中的 message(default "最新" = thread currentId)默认展开
// - 历史消息默认折叠
// - Reply Composer 不在 thread 内,固定在 Detail 底部(交给 MailV2Page layout)
// - 点击 thread item → 调用 onSelectThreadMessage(id),让 MailV2Page 切 selectedMessageId
//   (现实场景下用户大概率不会在这切,只是偶尔;Gmail 也是切去 detail 然后再回来)
//
// Props:
// - thread: list of MessageV2View ordered ASC(oldest→newest)
// - currentId: highlighted message
// - rootId: oldest message (root)
// - onSelectThreadMessage: navigate callback
// - onRefresh: 触发后端 reload(可选)

import React, { useMemo, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import type { MessageV2View } from "../api/mappers";

interface MailV2ThreadProps {
  thread: MessageV2View[];
  currentId: string | null;
  rootId: string | null;
  onSelectThreadMessage: (id: string) => void;
}

function formatDate(iso: string | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const mi = String(d.getUTCMinutes()).padStart(2, "0");
  return `${yyyy}/${mm}/${dd} ${hh}:${mi}`;
}

function senderLabel(m: MessageV2View): string {
  if (m.direction === "outbound") return "私";
  return m.senderName || m.senderEmail || "(unknown)";
}

function directionLabel(m: MessageV2View): string {
  if (m.direction === "outbound") return "送信済み";
  return "受信";
}

function initials(label: string): string {
  return label.trim().charAt(0).toUpperCase() || "?";
}

export function MailV2Thread({
  thread,
  currentId,
  rootId,
  onSelectThreadMessage,
}: MailV2ThreadProps) {
  // Track which historical message indices are expanded (each history is
  // independently collapsible). Default: nothing expanded.
  const [expandedHistory, setExpandedHistory] = useState<Set<number>>(
    () => new Set(),
  );

  const toggleHistory = (idx: number) => {
    setExpandedHistory((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const sortedThread = useMemo(() => {
    // Backend guarantees ASC; but be defensive.
    return [...thread].sort((a, b) => a.receivedAt.localeCompare(b.receivedAt));
  }, [thread]);

  if (sortedThread.length === 0) return null;
  if (sortedThread.length === 1 && sortedThread[0].id === currentId) {
    // Single message in thread = no UI element (it's just the current message
    // rendered in the body). Show only a brief summary.
    return (
      <div className="mv2-thread-summary" data-testid="mv2-thread-summary">
        <span>この会話には1件のメールがあります</span>
      </div>
    );
  }

  const currentIdx = sortedThread.findIndex((m) => m.id === currentId);
  const latestIdx = sortedThread.length - 1;
  // Latest message shown at top is always sortedThread[latestIdx] IF the
  // current message is the newest. If the user selected an older message in
  // the chain, that one is "latest" (always the currently displayed body).
  const topIdx = currentIdx >= 0 ? currentIdx : latestIdx;
  const topMsg = sortedThread[topIdx];
  const previousMsgs = sortedThread.filter((_, i) => i !== topIdx);

  return (
    <div className="mv2-thread" data-testid="mv2-thread">
      <div className="mv2-thread-summary" data-testid="mv2-thread-summary">
        <span>この会話には{sortedThread.length}件のメールがあります</span>
      </div>

      {/* ──────── Latest (current) message — expanded ──────── */}
      <div
        className="mv2-thread-latest"
        data-testid="mv2-thread-latest"
        onClick={() => onSelectThreadMessage(topMsg.id)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onSelectThreadMessage(topMsg.id);
          }
        }}
      >
        <div className="mv2-thread-msg-header">
          <div className="mv2-thread-avatar">{initials(senderLabel(topMsg))}</div>
          <div className="mv2-thread-msg-meta">
            <span className="mv2-thread-msg-sender">{senderLabel(topMsg)}</span>
            <span className="mv2-thread-msg-direction">{directionLabel(topMsg)}</span>
            <span className="mv2-thread-msg-date">{formatDate(topMsg.receivedAt)}</span>
          </div>
        </div>
        <div
          className="mv2-thread-msg-body"
          // eslint-disable-next-line react/no-danger -- sanitized backend HTML
          dangerouslySetInnerHTML={{ __html: topMsg.bodyHtml || topMsg.bodyText || topMsg.preview || "(empty body)" }}
        />
      </div>

      {/* ──────── Previous N messages — collapsed by default ──────── */}
      {previousMsgs.length > 0 && (
        <PreviousMessages
          messages={previousMsgs}
          expandedHistory={expandedHistory}
          onToggle={toggleHistory}
          onSelectThreadMessage={onSelectThreadMessage}
        />
      )}
    </div>
  );
}

interface PreviousMessagesProps {
  messages: MessageV2View[];
  expandedHistory: Set<number>;
  onToggle: (idx: number) => void;
  onSelectThreadMessage: (id: string) => void;
}

function PreviousMessages({
  messages,
  expandedHistory,
  onToggle,
  onSelectThreadMessage,
}: PreviousMessagesProps) {
  const [isOpen, setIsOpen] = useState(false);
  const hasAnyExpanded = (idx: number) => expandedHistory.has(idx);

  return (
    <div className="mv2-thread-previous">
      <button
        type="button"
        className="mv2-thread-previous-toggle"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen((v) => !v);
        }}
        aria-expanded={isOpen}
        data-testid="mv2-thread-previous-toggle"
      >
        {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        <span>{isOpen ? "過去のメールを閉じる" : `過去のメール（${messages.length}件）`}</span>
      </button>
      {isOpen && (
        <div className="mv2-thread-previous-list" data-testid="mv2-thread-previous-list">
          {messages.map((m, idx) => {
            const open = hasAnyExpanded(idx);
            return (
              <div
                key={m.id}
                className={`mv2-thread-msg ${open ? "is-expanded" : "is-collapsed"}`}
                data-testid="mv2-thread-msg"
              >
                <div
                  className="mv2-thread-msg-header"
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggle(idx);
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onToggle(idx);
                    }
                  }}
                >
                  <div className="mv2-thread-avatar small">
                    {initials(senderLabel(m))}
                  </div>
                  <div className="mv2-thread-msg-meta">
                    <span className="mv2-thread-msg-sender">{senderLabel(m)}</span>
                    <span className="mv2-thread-msg-direction">{directionLabel(m)}</span>
                    <span className="mv2-thread-msg-date">{formatDate(m.receivedAt)}</span>
                  </div>
                  {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </div>
                {open && (
                  <div className="mv2-thread-msg-body">
                    <div
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{
                        __html:
                          m.bodyHtml ||
                          m.bodyText ||
                          m.preview ||
                          "(empty body)",
                      }}
                    />
                    <button
                      type="button"
                      className="mv2-thread-msg-jump"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectThreadMessage(m.id);
                      }}
                    >
                      詳細を開く
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
